/**
 * Burn Storage Utility
 *
 * Persists burn transaction data to localStorage for recovery purposes.
 * Uses individual keys per transfer for fault isolation - if one entry
 * gets corrupted, others remain intact.
 *
 * Key pattern: parse_pending_${uuid}
 *
 * Status flow:
 * - 'pending': Saved before burn TX is sent (captures intent)
 * - 'burned': Burn TX confirmed on-chain
 * - 'submitted': Successfully POSTed to backend
 * - 'submission_failed': Backend POST failed after retries
 */

import type { Chain, TransferStatus } from '@/types'

const STORAGE_PREFIX = 'parse_pending_'
const MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours for pending transfers
const TERMINAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 days for completed/failed transfers

export type PendingStatus = 'pending' | 'burned' | 'submitted' | 'submission_failed'

export interface StoredBurn {
  id: string // UUID for this pending transfer
  burnTxHash?: string // Set after burn TX confirms
  sourceChain: Chain
  destinationChain: Chain
  sourceAddress: string
  destinationAddress: string
  amount: string
  createdAt: number
  updatedAt: number
  pendingStatus: PendingStatus // Local status tracking
  backendStatus?: TransferStatus // Status from backend when checked
  error?: string // Error message if submission failed
  retryCount?: number // Number of backend submission attempts
  intentId?: string // Intent ID for updating status after mint completes
}

/**
 * Generate a UUID for a new pending transfer
 */
function generateId(): string {
  return crypto.randomUUID()
}

/**
 * Get storage key for a transfer
 */
function getKey(id: string): string {
  return `${STORAGE_PREFIX}${id}`
}

/**
 * Get all stored burns from localStorage
 */
export function getStoredBurns(): StoredBurn[] {
  const burns: StoredBurn[] = []
  const now = Date.now()

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const data = localStorage.getItem(key)
          if (data) {
            const burn: StoredBurn = JSON.parse(data)
            // Filter out stale entries
            if (now - burn.createdAt < MAX_AGE_MS) {
              burns.push(burn)
            } else {
              // Clean up stale entry
              localStorage.removeItem(key)
            }
          }
        } catch (e) {
          console.warn(`Failed to parse burn entry ${key}, removing:`, e)
          localStorage.removeItem(key)
        }
      }
    }
  } catch (e) {
    console.warn('Failed to read burn storage:', e)
  }

  // Sort by creation time (newest first)
  return burns.sort((a, b) => b.createdAt - a.createdAt)
}

/**
 * Get a specific burn by ID
 */
export function getBurnById(id: string): StoredBurn | null {
  try {
    const data = localStorage.getItem(getKey(id))
    if (data) {
      return JSON.parse(data)
    }
  } catch (e) {
    console.warn(`Failed to get burn ${id}:`, e)
  }
  return null
}

/**
 * Get a burn by its transaction hash
 */
export function getBurnByTxHash(burnTxHash: string): StoredBurn | null {
  const burns = getStoredBurns()
  return burns.find(b => b.burnTxHash === burnTxHash) || null
}

/**
 * Create a new pending burn entry BEFORE the burn transaction
 * This captures user intent so we can recover if something fails
 */
export function createPendingBurn(params: {
  sourceChain: Chain
  destinationChain: Chain
  sourceAddress: string
  destinationAddress: string
  amount: string
}): StoredBurn {
  const now = Date.now()
  const burn: StoredBurn = {
    id: generateId(),
    ...params,
    createdAt: now,
    updatedAt: now,
    pendingStatus: 'pending',
    retryCount: 0,
  }

  try {
    localStorage.setItem(getKey(burn.id), JSON.stringify(burn))
    console.log(`📝 Created pending burn: ${burn.id}`)
  } catch (e) {
    console.warn('Failed to save pending burn:', e)
  }

  return burn
}

/**
 * Update a burn entry with the intent ID
 * Called after intent is created via API
 */
export function updateBurnIntentId(id: string, intentId: string): StoredBurn | null {
  try {
    const burn = getBurnById(id)
    if (burn) {
      burn.intentId = intentId
      burn.updatedAt = Date.now()
      localStorage.setItem(getKey(id), JSON.stringify(burn))
      console.log(`📋 Intent linked to burn: ${id} → ${intentId.slice(0, 8)}...`)
      return burn
    }
  } catch (e) {
    console.warn(`Failed to update intent ID ${id}:`, e)
  }
  return null
}

/**
 * Update a burn entry after the burn TX confirms
 */
export function markBurnConfirmed(id: string, burnTxHash: string): StoredBurn | null {
  try {
    const burn = getBurnById(id)
    if (burn) {
      burn.burnTxHash = burnTxHash
      burn.pendingStatus = 'burned'
      burn.updatedAt = Date.now()
      localStorage.setItem(getKey(id), JSON.stringify(burn))
      console.log(`🔥 Burn confirmed: ${id} → ${burnTxHash.slice(0, 10)}...`)
      return burn
    }
  } catch (e) {
    console.warn(`Failed to mark burn confirmed ${id}:`, e)
  }
  return null
}

/**
 * Mark a burn as successfully submitted to backend
 */
export function markBurnSubmitted(id: string): void {
  try {
    const burn = getBurnById(id)
    if (burn) {
      burn.pendingStatus = 'submitted'
      burn.updatedAt = Date.now()
      localStorage.setItem(getKey(id), JSON.stringify(burn))
      console.log(`📤 Burn submitted to backend: ${id}`)
    }
  } catch (e) {
    console.warn(`Failed to mark burn submitted ${id}:`, e)
  }
}

/**
 * Mark a burn submission as failed
 */
export function markSubmissionFailed(id: string, error: string, retryCount: number): void {
  try {
    const burn = getBurnById(id)
    if (burn) {
      burn.pendingStatus = 'submission_failed'
      burn.error = error
      burn.retryCount = retryCount
      burn.updatedAt = Date.now()
      localStorage.setItem(getKey(id), JSON.stringify(burn))
      console.log(`❌ Burn submission failed: ${id} (${retryCount} attempts)`)
    }
  } catch (e) {
    console.warn(`Failed to mark submission failed ${id}:`, e)
  }
}

/**
 * Update backend status of a stored burn
 */
export function updateBackendStatus(id: string, status: TransferStatus): void {
  try {
    const burn = getBurnById(id)
    if (burn) {
      burn.backendStatus = status
      burn.updatedAt = Date.now()
      localStorage.setItem(getKey(id), JSON.stringify(burn))
    }
  } catch (e) {
    console.warn(`Failed to update backend status ${id}:`, e)
  }
}

/**
 * Remove a burn from storage (when complete or dismissed)
 */
export function removeBurn(id: string): void {
  try {
    localStorage.removeItem(getKey(id))
    console.log(`🗑️ Removed burn: ${id}`)
  } catch (e) {
    console.warn(`Failed to remove burn ${id}:`, e)
  }
}

/**
 * Remove a burn by its transaction hash
 */
export function removeBurnByTxHash(burnTxHash: string): void {
  const burn = getBurnByTxHash(burnTxHash)
  if (burn) {
    removeBurn(burn.id)
  }
}

/**
 * Get burns for a specific address (source or destination)
 */
export function getBurnsForAddress(address: string): StoredBurn[] {
  const burns = getStoredBurns()
  const normalizedAddress = address.toLowerCase()

  return burns.filter(burn =>
    burn.sourceAddress.toLowerCase() === normalizedAddress ||
    burn.destinationAddress.toLowerCase() === normalizedAddress
  )
}

/**
 * Get burns that need recovery (burned but not submitted, or submission failed)
 */
export function getBurnsNeedingRecovery(): StoredBurn[] {
  const burns = getStoredBurns()
  return burns.filter(burn =>
    burn.pendingStatus === 'burned' ||
    burn.pendingStatus === 'submission_failed'
  )
}

/**
 * Get all pending burns (not yet confirmed on-chain)
 */
export function getPendingBurns(): StoredBurn[] {
  const burns = getStoredBurns()
  return burns.filter(burn => burn.pendingStatus === 'pending')
}

/**
 * Clear all stored burns (for testing/debugging)
 */
export function clearAllBurns(): void {
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keys.push(key)
      }
    }
    keys.forEach(key => localStorage.removeItem(key))
    console.log(`🗑️ Cleared ${keys.length} burns from storage`)
  } catch (e) {
    console.warn('Failed to clear burn storage:', e)
  }
}

/**
 * Cleanup old terminal (completed/failed) transfers
 * Removes transfers older than 30 days that are in terminal state
 * This prevents localStorage from growing unbounded
 */
export function cleanupOldTerminalTransfers(): number {
  const now = Date.now()
  let cleanedCount = 0

  try {
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const data = localStorage.getItem(key)
          if (data) {
            const burn: StoredBurn = JSON.parse(data)
            const isTerminal = burn.backendStatus === 'mint_success' || burn.backendStatus === 'failed' || burn.backendStatus === 'recovered'
            const isOld = (now - burn.updatedAt) > TERMINAL_MAX_AGE_MS

            if (isTerminal && isOld) {
              keysToRemove.push(key)
            }
          }
        } catch (e) {
          // Skip malformed entries
        }
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
      cleanedCount++
    })

    if (cleanedCount > 0) {
      console.log(`🧹 Cleaned up ${cleanedCount} old terminal transfer(s) from localStorage`)
    }
  } catch (e) {
    console.warn('Failed to cleanup old transfers:', e)
  }

  return cleanedCount
}
